import { EProjectType, ESdChangeReason, IDevice } from "@kuika/kuika-cl-model";
import _ from "lodash";
import deviceListJson from "../../../../../shared/data/device-list.json";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { getSdInstance } from "../../../../../shared/utility/designer-context";
import { selectDesignerViewportDevice } from "../../../../../shared/utility/designer-viewport-actions";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import "./kuika-device.scss";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";

export interface IDeviceMenuDropdownState {
  deviceTypes: string[];
  isOpen: boolean;
  selectedType: string;
}

export interface IDeviceMenuDropdownProps {}

class DeviceMenuDropdown extends PureSdComponent<IDeviceMenuDropdownProps & IWithSDProps, IDeviceMenuDropdownState> {
  deviceList: IDevice[] = (deviceListJson as unknown as IDevice[]) || [];

  constructor(props: IDeviceMenuDropdownProps & IWithSDProps) {
    super(props);
    const deviceTypes: string[] = _.keys(
      _.countBy(this.deviceList || [], (device) => {
        return device?.type || "Desktop";
      })
    );
    const selectedDevice = this.getSelectedDevice();
    const initialType = selectedDevice?.type || "Desktop";
    this.state = { deviceTypes, isOpen: false, selectedType: initialType };
  }

  componentDidMount() {
    const sd = this.props.sd?.getScreenDesign();
    if (!sd) return;

    if (!_.isEmpty(this.props.sd?.project)) {
      this.initializeDeviceSelection();
    }
  }

  componentDidUpdate(prevProps: IDeviceMenuDropdownProps & IWithSDProps) {
    if (_.isEmpty(prevProps.sd?.project) && !_.isEmpty(this.props.sd?.project)) {
      this.initializeDeviceSelection();
    }

    if (prevProps.sd?.selectedDevice !== this.props.sd?.selectedDevice) {
      this.setState({ selectedType: this.props.sd?.selectedDevice?.type || "Desktop" });
    }
  }

  private initializeDeviceSelection = () => {
    const selectedDevice = this.getSelectedDevice();
    if (selectedDevice) {
      if (!this.props.sd?.selectedDevice || this.props.sd?.selectedDevice?.id === "custom") {
        if (this.props.sd?.project?.projectType === EProjectType.Mobile) {
          const phoneDevices = this.getDevicesByType("Phone");
          if (phoneDevices.length > 0) {
            this.handleSelectDevice(phoneDevices[0]);
            return;
          }
        } else {
          const desktopDevices = this.getDevicesByType("Desktop");
          const hdPlusDevice = desktopDevices.find((d) => d?.model === "Full HD");
          if (hdPlusDevice) {
            this.handleSelectDevice(hdPlusDevice);
            return;
          }
        }
        this.handleSelectDevice(selectedDevice);
      }
    }
  };

  handleSelectDevice = (device: IDevice) => {
    if (!device || !this.deviceList?.length) return;

    const devices = this.deviceList.filter((d: IDevice) => d?.id === device.id);
    if (!devices?.length || !this.props.sd?.setSpecificDevice) return;

    selectDesignerViewportDevice(getSdInstance(), devices[0]);
    this.setState({ selectedType: devices[0]?.type || "Desktop" });
  };

  handleDeviceTypeChange = (type: string) => {
    if (!type) return;
    this.setState({ selectedType: type });

    const devicesByType = this.getDevicesByType(type);
    if (devicesByType.length > 0) {
      let deviceToSelect;

      if (type === "Desktop" && devicesByType.length > 1) {
        deviceToSelect = devicesByType?.find((d) => d?.model === "Full HD");
      } else if (type === "Tablet" || type === "Phone") {
        deviceToSelect = devicesByType[0];
      }

      if (deviceToSelect) {
        this.handleSelectDevice(deviceToSelect);
      }
    }
  };

  getSelectedDevice = (): IDevice => {
    if (!this.deviceList?.length)
      return {
        id: "",
        type: "Desktop",
        model: "Default",
        viewport: "1920x1080",
        css: "",
        subType: "",
        resolution: "1920x1080"
      };

    return this.props?.sd?.selectedDevice || this.deviceList[1];
  };

  handleOpenChange = (isOpen: boolean) => {
    this.setState({ isOpen });
  };

  getDevicesByType = (type: string): IDevice[] => {
    if (!type || !this.deviceList?.length) return [];

    return this.deviceList.filter((device) => device?.type === type && device?.id !== "custom");
  };

  render() {
    const sd = this.props.sd?.getScreenDesign();
    if (!sd) return null;

    const selectedDevice: IDevice = this.getSelectedDevice();
    const availableTypes = (this.state.deviceTypes || [])
      .filter((type) => {
        if (!type || !sd.devices) return false;
        if (type === "Desktop" && sd.devices.desktop) return true;
        if (type === "Tablet" && sd.devices.tablet) return true;
        if (type === "Phone" && sd.devices.phone) return true;
        return false;
      })
      .sort((a, b) => {
        const order = { Desktop: 1, Tablet: 2, Phone: 3 };
        return (order[a as keyof typeof order] || 0) - (order[b as keyof typeof order] || 0);
      });

    const currentTypeDevices = this.getDevicesByType(this.state.selectedType);
    const deviceOptions = currentTypeDevices.map((device) => ({
      key: device?.id || "",
      value: device?.id || "",
      label: device?.model || "",
      rightSlot: device?.viewport?.toLowerCase() !== "custom" ? device.viewport : null,
      showSelectedIcon: true
    }));

    const dropdownHeader =
      availableTypes.length > 0 ? (
        <div className="kuika-device__dropdown-header">
          <KuikaTabs
            options={availableTypes}
            onChange={this.handleDeviceTypeChange}
            value={this.state.selectedType}
            defaultValue={selectedDevice?.id !== "custom" ? selectedDevice?.type : "Desktop"}
          />
        </div>
      ) : null;

    return (
      <div className="kuika-device">
        <KuikaDropdownMenu
          options={deviceOptions}
          open={this.state.isOpen}
          setOpen={this.handleOpenChange}
          overlayClassName="kuika-device__dropdown-overlay"
          header={dropdownHeader}
          selected={this.props.sd?.selectedDevice?.id}
          setSelected={(key) => {
            const selectedDevice: any = this.deviceList.find((option: any) => option?.id === key);
            this.handleSelectDevice(selectedDevice);
          }}
        >
          <KuikaTooltip content={selectedDevice?.model || ""}>
            <div
              data-testid="deviceSelectorDropdownButton"
              data-onboarding-id="header_toolbar-right_device-selector"
              className="kuika-device__selector"
            >
              <KuikaSvg
                width={12}
                height={12}
                name={
                  selectedDevice?.type === "Desktop"
                    ? "device-imac"
                    : selectedDevice?.type === "Tablet"
                      ? "device-tablet"
                      : "device-mobile"
                }
                data-testid={`${selectedDevice.type.toLowerCase()}Icon`}
                data-onboarding-id={`header_toolbar-right_device-${selectedDevice.type.toLowerCase()}`}
              />
              <KuikaSvg name={this.state.isOpen ? "chevron-up" : "chevron-down"} type="arrows" width={12} height={12} />
            </div>
          </KuikaTooltip>
        </KuikaDropdownMenu>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const deviceMenuDropdown = withSD<IDeviceMenuDropdownProps>(DeviceMenuDropdown);
export { deviceMenuDropdown as DeviceMenuDropdown };
